import { z } from 'zod';

import {
  httpMethodSchema,
  parametersSchemaDefinition,
  responseMappingSchema,
  displayConfigSchema,
} from './custom-tool.schema';

// Chatbot tools belong to the chat (assistant) agent and are independent of the
// voice `custom_tools`. `tool_type` selects the executor.
// 'http' = call an endpoint, 'sql' = guarded read-only query, 'pdf' = hand the
// given data to the client to render + download as a PDF (no server call).
export const chatbotToolTypeSchema = z.enum(['http', 'sql', 'pdf']);
export type ChatbotToolType = z.infer<typeof chatbotToolTypeSchema>;

const nameSchema = z
  .string()
  .min(2, 'Tool name must be at least 2 characters')
  .max(100, 'Tool name must be at most 100 characters')
  .regex(
    /^[a-z_][a-z0-9_]*$/,
    'Tool name must be snake_case (lowercase letters, numbers, and underscores only)',
  );

// Base record (DB shape)
export const chatbotToolSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  tool_type: chatbotToolTypeSchema,
  http_method: httpMethodSchema.nullable().optional(),
  url: z.string().nullable().optional(),
  headers: z.record(z.string(), z.string()).nullable().optional(),
  parameters_schema: parametersSchemaDefinition.nullable().optional(),
  response_mapping: responseMappingSchema.nullable().optional(),
  display_config: displayConfigSchema.nullable().optional(),
  sql_query: z.string().nullable().optional(),
  is_mutation: z.boolean(),
  is_active: z.boolean(),
  created_at: z.date().or(z.string().datetime()),
  updated_at: z.date().or(z.string().datetime()),
});
export type ChatbotTool = z.infer<typeof chatbotToolSchema>;

// Shared create/update body, validated per tool_type by the refinement below.
const writeShape = {
  name: nameSchema,
  description: z.string().min(10, 'Description must be at least 10 characters'),
  tool_type: chatbotToolTypeSchema.default('http'),
  http_method: httpMethodSchema.nullable().optional(),
  url: z.string().url('Invalid URL format').nullable().optional(),
  headers: z.record(z.string(), z.string()).nullable().optional(),
  parameters_schema: parametersSchemaDefinition.nullable().optional(),
  response_mapping: responseMappingSchema.nullable().optional(),
  display_config: displayConfigSchema.nullable().optional(),
  sql_query: z.string().nullable().optional(),
  is_mutation: z.boolean().optional().default(false),
  is_active: z.boolean().optional().default(true),
};

function refineByType<T extends z.ZodTypeAny>(schema: T) {
  return schema.superRefine((value, ctx) => {
    const val = value as Record<string, unknown>;
    if (val.tool_type === 'http') {
      if (!val.http_method)
        ctx.addIssue({
          code: 'custom',
          path: ['http_method'],
          message: 'http_method is required for http tools',
        });
      if (!val.url)
        ctx.addIssue({ code: 'custom', path: ['url'], message: 'url is required for http tools' });
    }
    if (val.tool_type === 'sql' && !val.sql_query) {
      ctx.addIssue({
        code: 'custom',
        path: ['sql_query'],
        message: 'sql_query is required for sql tools',
      });
    }
  });
}

export const createChatbotToolSchema = refineByType(z.object(writeShape));
export type CreateChatbotToolDto = z.infer<typeof createChatbotToolSchema>;

export const updateChatbotToolSchema = refineByType(
  z
    .object({
      ...writeShape,
      name: nameSchema.optional(),
      description: z.string().min(10).optional(),
      tool_type: chatbotToolTypeSchema.optional(),
      is_mutation: z.boolean().optional(),
      is_active: z.boolean().optional(),
    })
    .partial(),
);
export type UpdateChatbotToolDto = z.infer<typeof updateChatbotToolSchema>;

export const chatbotToolQuerySchema = z.object({
  search: z.string().optional(),
  tool_type: chatbotToolTypeSchema.optional(),
  is_active: z.coerce.boolean().optional(),
});
export type ChatbotToolQuery = z.infer<typeof chatbotToolQuerySchema>;

// Test request: run an http or sql tool ad-hoc with sample args.
export const testChatbotToolSchema = z.object({
  tool_type: chatbotToolTypeSchema,
  http_method: httpMethodSchema.optional(),
  url: z.string().url().optional(),
  headers: z.record(z.string(), z.string()).optional(),
  parameters_schema: parametersSchemaDefinition.optional(),
  response_mapping: responseMappingSchema.optional(),
  sql_query: z.string().optional(),
  args: z.record(z.string(), z.unknown()).optional().default({}),
});
export type TestChatbotToolDto = z.infer<typeof testChatbotToolSchema>;

export interface TestChatbotToolResponse {
  success: boolean;
  output?: unknown;
  rows?: unknown[];
  row_count?: number;
  truncated?: boolean;
  error?: string;
}

// Export/import payload (transfer between environments).
export const chatbotToolExportItemSchema = z.object({
  name: nameSchema,
  description: z.string(),
  tool_type: chatbotToolTypeSchema,
  http_method: httpMethodSchema.nullable().optional(),
  url: z.string().nullable().optional(),
  headers: z.record(z.string(), z.string()).nullable().optional(),
  parameters_schema: parametersSchemaDefinition.nullable().optional(),
  response_mapping: responseMappingSchema.nullable().optional(),
  display_config: displayConfigSchema.nullable().optional(),
  sql_query: z.string().nullable().optional(),
  is_mutation: z.boolean().optional().default(false),
  is_active: z.boolean().optional().default(true),
});
export const chatbotToolImportSchema = z.object({
  tools: z.array(chatbotToolExportItemSchema).min(1, 'At least one tool is required'),
});
export type ChatbotToolImportDto = z.infer<typeof chatbotToolImportSchema>;
